import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * Admin-only dialog for editing every field on a booking — customer name/phone,
 * pickup/dropoff addresses, schedule, price, distance, payment status, notes.
 *
 * Only changed fields are sent to the server (we diff against `initialBooking`)
 * so the audit log on the backend stays clean. On success the parent gets
 * `onSaved(updatedBooking)` and is expected to re-fetch the list.
 *
 * Address fields here are plain text — the server-side stops endpoint is the
 * one that requires geocoded coords. Editing pickup/dropoff text only updates
 * the display address; if you need to re-route the GPS routing, also re-pick
 * the address in the customer-facing booking flow (rare case).
 */
const EDITABLE_FIELDS = [
  "customer_name", "customer_phone", "customer_email",
  "pickup_address", "dropoff_address",
  "scheduled_date", "scheduled_time",
  "distance_miles", "estimated_price",
  "payment_method", "payment_status",
  "notes",
];

const PAYMENT_STATUSES = ["unpaid", "pending", "paid", "cash", "jim_card", "refunded", "failed"];
const PAYMENT_METHODS = ["stripe", "cash", "jim", "credits"];

export default function EditBookingDialog({ booking, open, onClose, onSaved }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!booking) return;
    // Seed the draft with the booking's current values, but only for fields we
    // expose — so we don't accidentally PATCH internal flags.
    const seed = {};
    for (const f of EDITABLE_FIELDS) seed[f] = booking[f] ?? "";
    setDraft(seed);
  }, [booking, open]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!booking) return;
    // Build the patch: only include fields the admin actually changed. Empty
    // string is treated as "unchanged" rather than "clear to null" so we don't
    // wipe required fields by accident.
    const patch = {};
    for (const f of EDITABLE_FIELDS) {
      const v = draft[f];
      if (v === "" || v == null) continue;
      // numeric fields
      if (f === "distance_miles" || f === "estimated_price") {
        const num = parseFloat(v);
        if (!isNaN(num) && num !== Number(booking[f])) patch[f] = num;
        continue;
      }
      if (String(v) !== String(booking[f] ?? "")) patch[f] = v;
    }
    if (Object.keys(patch).length === 0) {
      toast.info("No changes to save");
      onClose();
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/bookings/${booking.id}`, patch);
      // Build an "undo patch" — the previous value for every field that
      // actually changed — so the parent can restore in one click.
      const undoPatch = {};
      for (const k of data.changed_fields || []) {
        if (k in booking) undoPatch[k] = booking[k];
      }
      toast.success(`Saved · ${data.changed_fields.length} field${data.changed_fields.length === 1 ? "" : "s"} updated`);
      onSaved?.(data.booking, { undoPatch, bookingId: booking.id, changedFields: data.changed_fields });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-booking-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Edit booking · {booking.id?.slice(0, 8)}</DialogTitle>
          <p className="text-xs text-zinc-400">
            Only changed fields are saved. Price changes auto-recompute subtotal + sales tax.
          </p>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <Field id="customer_name" label="Customer name" value={draft.customer_name} onChange={(v) => set("customer_name", v)} />
          <div className="grid grid-cols-2 gap-3">
            <Field id="customer_phone" label="Phone" value={draft.customer_phone} onChange={(v) => set("customer_phone", v)} />
            <Field id="customer_email" label="Email" value={draft.customer_email} onChange={(v) => set("customer_email", v)} />
          </div>
          <Field id="pickup_address" label="Pickup address" value={draft.pickup_address} onChange={(v) => set("pickup_address", v)} />
          <Field id="dropoff_address" label="Dropoff address" value={draft.dropoff_address} onChange={(v) => set("dropoff_address", v)} />
          <div className="grid grid-cols-2 gap-3">
            <Field id="scheduled_date" label="Date" placeholder="YYYY-MM-DD" value={draft.scheduled_date} onChange={(v) => set("scheduled_date", v)} />
            <Field id="scheduled_time" label="Time" placeholder="HH:MM" value={draft.scheduled_time} onChange={(v) => set("scheduled_time", v)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field id="distance_miles" label="Distance (miles)" type="number" value={draft.distance_miles} onChange={(v) => set("distance_miles", v)} />
            <Field id="estimated_price" label="Total price ($)" type="number" value={draft.estimated_price} onChange={(v) => set("estimated_price", v)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              id="payment_method"
              label="Payment method"
              value={draft.payment_method}
              options={PAYMENT_METHODS}
              onChange={(v) => set("payment_method", v)}
            />
            <SelectField
              id="payment_status"
              label="Payment status"
              value={draft.payment_status}
              options={PAYMENT_STATUSES}
              onChange={(v) => set("payment_status", v)}
            />
          </div>
          <Field id="notes" label="Notes" value={draft.notes} onChange={(v) => set("notes", v)} />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-white/15 text-zinc-300 hover:bg-zinc-800" data-testid="edit-booking-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#0047FF] hover:bg-[#003BE0] text-white" data-testid="edit-booking-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ id, label, value, onChange, type = "text", placeholder }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">{label}</Label>
    <Input
      id={id}
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="bg-zinc-900 border-white/10 text-white"
      data-testid={`edit-field-${id}`}
    />
  </div>
);

const SelectField = ({ id, label, value, options, onChange }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">{label}</Label>
    <select
      id={id}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-zinc-900 border border-white/10 text-white text-sm rounded-md px-3 py-2"
      data-testid={`edit-field-${id}`}
    >
      <option value="" disabled>—</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </div>
);
